import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';

interface AISuggestionsProps {
  onSelect: (val: string) => void;
  onDeleteRecent: (val: string) => void;
  recentSearches: string[];
  suggestions: string[];
}

export default function AISuggestions({
  onSelect,
  onDeleteRecent,
  recentSearches = [],
  suggestions = [],
}: AISuggestionsProps) {
  return (
    <Box sx={{ width: '100%', mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Dynamic suggestions header */}
      {suggestions.length > 0 && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#475569',
              fontWeight: 600,
              mb: 1.5,
            }}
          >
            <HelpOutlineIcon fontSize="small" sx={{ color: '#7c3aed' }} />
            Suggested queries:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {suggestions.map(suggestion => (
              <Chip
                key={suggestion}
                label={suggestion}
                onClick={() => onSelect(suggestion)}
                sx={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  color: '#475569',
                  fontWeight: 500,
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#f5f3ff',
                    borderColor: '#c084fc',
                    color: '#7c3aed',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 4px rgba(124, 58, 237, 0.08)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Recent Searches */}
      {recentSearches.length > 0 && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#475569',
              fontWeight: 600,
              mb: 1,
            }}
          >
            <HistoryIcon fontSize="small" sx={{ color: '#64748b' }} />
            Recent searches:
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              borderColor: '#e2e8f0',
              backgroundColor: '#ffffff',
            }}
          >
            <List disablePadding>
              {recentSearches.slice(0, 5).map((search, idx) => (
                <ListItem
                  key={`${search}-${idx}`}
                  disablePadding
                  divider={idx < Math.min(recentSearches.length, 5) - 1}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete recent search"
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteRecent(search);
                      }}
                      sx={{
                        color: '#94a3b8',
                        mr: 0.5,
                        '&:hover': {
                          color: '#ef4444',
                          backgroundColor: '#fef2f2',
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    onClick={() => onSelect(search)}
                    sx={{
                      py: 1,
                      px: 2,
                      '&:hover': {
                        backgroundColor: '#f8fafc',
                        '& .MuiSvgIcon-root': {
                          color: '#7c3aed',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: '36px' }}>
                      <HistoryIcon fontSize="small" sx={{ color: '#94a3b8', transition: 'color 0.2s ease' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={search}
                      primaryTypographyProps={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#334155',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
